import {
  BuildingOfficeIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { toast } from 'sonner';

import { PoliceDepartmentLink } from '@/components/entity-links';
import { ContactHandlesList } from '@/components/ui/ContactHandlesList';
import { ReadPoliceDepartment } from '@/services/api/generated/policeReportsService';
import { usePoliceDepartmentsTriggerEnrichmentMutation } from '@/services/api/police';

import { EnrichmentStatusIndicator } from '../../EnrichmentStatusIndicator';
import { RecordsChannelCard } from './RecordsChannelCard';

interface PoliceDepartmentSectionProps {
  department: ReadPoliceDepartment;
}

export const PoliceDepartmentSection = ({
  department,
}: PoliceDepartmentSectionProps) => {
  const [triggerEnrichment] = usePoliceDepartmentsTriggerEnrichmentMutation();
  const [showContacts, setShowContacts] = useState(false);

  const handleEnrichmentAction = async (departmentId: string) => {
    try {
      await triggerEnrichment({ id: departmentId }).unwrap();
      toast.success('Department enrichment started');
    } catch (error) {
      toast.error('Failed to start enrichment');
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">
              <PoliceDepartmentLink
                entityId={department.id}
                className="text-gray-900 hover:text-blue-700 decoration-gray-300"
              >
                {department.name || 'Police Department'}
              </PoliceDepartmentLink>
            </h2>
          </div>
          <EnrichmentStatusIndicator
            department={department}
            onActionClick={handleEnrichmentAction}
          />
        </div>
      </div>

      {/* Body */}
      <div className="p-6 space-y-6">
        {/* Records channels */}
        {department.records_channels &&
          department.records_channels.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                Request Records
              </h4>
              <div className="space-y-2">
                {department.records_channels.map((channel) => (
                  <RecordsChannelCard
                    key={channel.id}
                    channel={channel as any}
                  />
                ))}
              </div>
            </div>
          )}

        {/* Other contact handles (collapsed by default) */}
        {department.handles && department.handles.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setShowContacts((prev) => !prev)}
              className="flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-800 focus:outline-none"
              aria-expanded={showContacts}
              aria-controls="other-contacts"
            >
              <ChevronDownIcon
                className={`h-3 w-3 transition-transform ${
                  showContacts ? 'rotate-180' : 'rotate-0'
                }`}
              />
              <span>Other contacts ({department.handles.length})</span>
            </button>

            {showContacts && (
              <div className="mt-2">
                <ContactHandlesList
                  handles={(department.handles as any) || []}
                  onUpdate={async () => {}}
                  title=""
                  inline
                  readOnly
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
